import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../../Components/Ui/Card';
import Button from '../../../Components/Ui/Button';
// import StockIndicator from "../../../Components/Ui/StockIndicator";
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useGetProductByIdQuery } from '../../../app/services/productApi';

const ProductDetails = () => {
  const { id } = useParams();
  const { data, isLoading, error } = useGetProductByIdQuery(id);
  const navigate = useNavigate();

  const product = data?.data;
  const [selectedImage, setSelectedImage] = useState(0);

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-bold text-gray-600">Loading Product...</h2>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-bold text-red-600">Error Loading Product</h2>
          <p className="text-gray-600 mt-2">{error?.data?.message || 'Something went wrong'}</p>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
        </Card>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Card className="p-6 text-center">
          <h2 className="text-xl font-bold text-red-600">Product Not Found</h2>
          <Button onClick={() => navigate(-1)} className="mt-4">
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
        </Card>
      </div>
    );
  }

  const productImages = product.images?.length
    ? product.images.map((img) => (typeof img === 'string' ? img : img.url)) // Handle both string and object formats
    : ['https://via.placeholder.com/500x500?text=No+Image'];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Product Details</h2>
        <div className="flex gap-4">
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft size={16} />
            Back
          </Button>
          <Button onClick={() => navigate(`/brand/products/${id}/history`)}>
            Product History
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-10">
        <div className="bg-gray-100 rounded-3xl p-8 flex flex-col items-center">
          <div className="relative w-full aspect-square max-w-lg group mb-4">
            <img
              src={productImages[selectedImage]}
              alt={product.title}
              className="object-contain w-full h-full rounded-2xl shadow-lg transition-transform duration-300 group-hover:scale-105"
            />
            <div className="absolute bottom-0 left-0 right-0 z-20 primary-background white-text text-center py-2 text-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              {product.title}
            </div>
          </div>

          <div className="flex gap-3 flex-wrap justify-center">
            {productImages.map((img, index) => (
              <img
                key={index}
                src={img}
                alt={`thumb-${index}`}
                onClick={() => setSelectedImage(index)}
                className={`w-20 h-20 object-cover rounded-xl border-2 cursor-pointer ${
                  selectedImage === index ? 'border-blue-500' : 'border-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <h3 className="text-3xl font-semibold text-gray-900">{product.title}</h3>
          <p className="text-gray-500 text-sm">SKU: {product.sku}</p>

          <p className="text-secondary text-md">
            <span className="font-medium text-gray-600">Brand:</span> {product.brand?.name || 'N/A'}
          </p>

          <p className="text-secondary text-md">
            <span className="font-medium text-gray-600">Category:</span>{' '}
            {product.category?.name || 'N/A'}
          </p>

          <p className="text-lg text-secondary">{product.description}</p>

          <div className="space-y-2">
            <div className="text-2xl font-bold text-green-600">
              ₹{product.basePrice?.toFixed(2) || 'N/A'}
            </div>
            {product.mrp && (
              <div className="text-lg text-gray-500 line-through">
                MRP: ₹{product.mrp.toFixed(2)}
              </div>
            )}
          </div>

          <div className="pt-4 border-t border-gray-200">
            <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
              <div>
                <p className="font-semibold tertiary-text-color">Status</p>
                <p
                  className={`font-semibold ${
                    product.status === 'active'
                      ? 'text-green-600'
                      : product.status === 'inactive'
                        ? 'text-yellow-600'
                        : product.status === 'discontinued'
                          ? 'text-red-600'
                          : 'text-gray-600'
                  }`}
                >
                  {product.status?.charAt(0).toUpperCase() + product.status?.slice(1)}
                </p>
              </div>

              <div>
                <p className="font-semibold tertiary-text-color">Packaging</p>
                <p>{(product.packagingType && product.packagingType.standard) || 'N/A'}</p>
              </div>

              <div>
                <p className="font-semibold tertiary-text-color">MOQ</p>
                <p>{product.baseMoq || 1}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetails;
